import { minimalInputTheme } from '@/app/theme';
import { useGetUserEmailsQuery } from '@/features/msGraph/msGraphApi';
import { Autocomplete, TextField } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

export const UserEmailSelect = ({
	delegatedEmails = [],
	disabled,
	onChange,
	minimal = false,
	value = null,   
	loading = false,
}) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userEmail } = useGetUserEmailsQuery(
		{ email },
		{ skip: !email }
	);


	const primaryEmail = useMemo(() => {
		if (!userEmail?.proxyAddresses?.length) return email ?? '';
		const primary = userEmail.proxyAddresses.find((addr) => addr.startsWith('SMTP:'));
		return primary ? primary.replace(/^SMTP:/, '').trim() : email ?? '';
	}, [userEmail?.proxyAddresses, email]);


	const options = useMemo(() => {
		const base = (userEmail?.proxyAddresses ?? [])
			.map((addr) => addr.replace(/^SMTP:/i, '').trim())
			.filter((addr) => !addr.endsWith('.onmicrosoft.com'))
			.map((email) => ({
				name: email,
				signature: email, 
			}));

		const delegated = delegatedEmails.map((email) => ({
			name: email,
			signature: email,
		}));

		return Array.from(
			new Map([...base, ...delegated].map((o) => [o.name, o])).values()
		);
	}, [userEmail?.proxyAddresses, delegatedEmails]);


	useEffect(() => {
		if (loading || value || options.length === 0) return;
		const primaryOption = options.find((o) => o.name === primaryEmail);
		if (primaryOption) {
			onChange(primaryOption);
		}
	}, [loading, onChange, options, primaryEmail, value]);

	return (
		<Autocomplete
			id="user-email-select"
			fullWidth
			disabled={disabled || loading || options.length === 0}
			options={options}
			value={value}
			isOptionEqualToValue={(option, val) => option.name === val?.name}
			getOptionLabel={(option) => option?.name ?? ''}
			filterOptions={(opts) => opts}
			disableClearable
			onChange={(_, option) => onChange(option)}
			renderOption={(props, option) => (
				<li {...props} key={`email-${option.name}`}>
					{option.name}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					variant={minimal ? 'standard' : 'outlined'}
					onKeyDown={(e) => e.preventDefault()}
					sx={(theme) => ({
						...(minimal && minimalInputTheme),
						'& .MuiInput-input': { color: theme.palette.primary.main },
						cursor: 'pointer',
					})}
					InputProps={{
						...params.InputProps,
						readOnly: true,
						style: { caretColor: 'transparent', cursor: 'pointer' },
					}}
				/>
			)}
			sx={{
				cursor: 'pointer',
				'& .MuiInputBase-root': { cursor: 'pointer' },
				'& input': { cursor: 'pointer' },
				...(minimal && {
					...minimalInputTheme,
					'& .MuiInputBase-root': { backgroundColor: 'transparent' },
				}),
			}}
		/>
	);
};
